/**
 * The one API client for the SPA.
 *
 * `cookieAuth()` per design §2.4: the web surface authenticates with a
 * better-auth session cookie and sends no credential header at all. Nothing here
 * reads or writes a token — if this file ever grows one, the web app has stopped
 * being the cookie client.
 */

import { type CrmClient, cookieAuth, createCrmClient } from '@crm/api-client';
import { createContext, useContext } from 'react';
import { API_BASE_URL, CLIENT_VERSION } from '../env.ts';
import { newCorrelationId, noteCorrelationId } from '../lib/diagnostics.ts';

export type { CrmClient };

/**
 * Set when the server answers 401. The connection strip reads it, so a dead
 * session is visible on the page rather than only in a failed request.
 *
 * A module-level store rather than React state on purpose: `onAuthError` fires
 * from inside the transport, which has no component to call `setState` on.
 */
let reauthNeeded = false;
const reauthListeners = new Set<(needed: boolean) => void>();

export function isReauthNeeded(): boolean {
  return reauthNeeded;
}

export function setReauthNeeded(needed: boolean): void {
  if (reauthNeeded === needed) return;
  reauthNeeded = needed;
  for (const listener of reauthListeners) {
    listener(needed);
  }
}

export function subscribeReauth(listener: (needed: boolean) => void): () => void {
  reauthListeners.add(listener);
  return () => reauthListeners.delete(listener);
}

export function createWebCrmClient(): CrmClient {
  return createCrmClient({
    baseUrl: API_BASE_URL,
    auth: cookieAuth(),
    client: 'web',
    clientVersion: CLIENT_VERSION,
    // Minted here rather than left to the client's default so the id is known to
    // THIS app before the request leaves — the error boundary can then name a
    // call whose response never arrived.
    idempotencyKeyFactory: newCorrelationId,
    onAuthError: (info) => {
      noteCorrelationId(info.correlationId);
      setReauthNeeded(true);
    },
  });
}

const CrmClientContext = createContext<CrmClient | null>(null);

export const CrmClientProvider = CrmClientContext.Provider;

/**
 * The client, from context.
 *
 * Context rather than a module singleton so a test can supply a stub with only
 * the two or three procedures the component under test calls, and so a future
 * tenant switch can hand down a differently-configured client without a reload.
 */
export function useCrmClient(): CrmClient {
  const client = useContext(CrmClientContext);
  if (client === null) {
    throw new Error('useCrmClient must be used inside <CrmClientProvider>');
  }
  return client;
}
